import React from 'react'
import type { ThreadSummary } from '../api/threads'
import useThreadsStore, { type ThreadFilters } from '../store/threads'

type ThreadLike = ThreadSummary & {
  has_flagged?: boolean
  has_on_air?: boolean
  latest_content?: string
  contact_name?: string
}

const normalize = (value?: string | null): string => (value ?? '').trim().toLowerCase()

export const matchesThreadFilters = (thread: ThreadLike, filters: ThreadFilters): boolean => {
  const hasStatusFilter = filters.unreadOnly || filters.flaggedOnly || filters.onAirOnly

  if (hasStatusFilter) {
    const matchesUnread = filters.unreadOnly && (thread.unread_count ?? 0) > 0
    const matchesFlagged = filters.flaggedOnly && Boolean(thread.has_flagged)
    const matchesOnAir = filters.onAirOnly && Boolean(thread.has_on_air)

    if (!(matchesUnread || matchesFlagged || matchesOnAir)) {
      return false
    }
  }

  const query = normalize(filters.q)
  if (query) {
    const identityNames = thread.identities?.map((identity) => identity.display_name ?? '') ?? []
    const haystack = [thread.contact_name ?? '', thread.latest_content ?? '', ...identityNames]
      .map(normalize)
      .filter((value) => value.length > 0)
      .join(' ')

    if (!haystack.includes(query)) {
      return false
    }
  }

  return true
}

export const filterThreads = (threads: ThreadSummary[], filters: ThreadFilters): ThreadSummary[] => {
  return threads.filter((thread) => matchesThreadFilters(thread, filters))
}

const useFilteredThreads = (): ThreadSummary[] => {
  const threads = useThreadsStore((state) => state.items)
  const filters = useThreadsStore((state) => state.filters)

  return React.useMemo(() => filterThreads(threads, filters), [threads, filters])
}

export default useFilteredThreads
